import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import queriesApi from '../../api/queries';

/**
 * @param {object} props
 * @param {boolean} [props.showTitle=true]
 * @param {function} [props.onRunQuery] - when provided, "Run Again" calls this with the query text
 *   instead of navigating to /query. Useful from overlays to open the QB overlay directly.
 */
const QueryHistoryContent = ({ showTitle = true, onRunQuery }) => {
  const [queries, setQueries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQueries = async () => {
      try {
        setIsLoading(true);
        const data = await queriesApi.getQueryHistory(50);
        setQueries(data);
      } catch (err) {
        console.error('Failed to fetch query history:', err);
        setError('Failed to load query history. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchQueries();
  }, []);

  if (isLoading) {
    return (
      <div className="p-6 text-center">
        <div className="inline-block w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p className="mt-2 text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Loading query history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div>
      {showTitle && (
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">Query History</h1>
        </div>
      )}

      {queries.length === 0 ? (
        <div className="p-6 text-center bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md">
          <p className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">No query history found. Start running queries to see them here!</p>
          <div className="mt-4">
            <Link to="/query" className="px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors">
              Run a Query
            </Link>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md">
          <div className="px-4 py-5 sm:p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-ui-light-border dark:divide-ui-dark-border">
                <thead className="bg-ui-light-hover dark:bg-ui-dark-hover">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">Query</th>
                    <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">Status</th>
                    <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">Time</th>
                    <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">Type</th>
                    <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">Format</th>
                    <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">Executed</th>
                    <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-right text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-ui-light-card dark:bg-ui-dark-card divide-y divide-ui-light-border dark:divide-ui-dark-border">
                  {queries.map((query) => (
                    <tr key={query.id} className="hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors">
                      <td className="px-6 py-4">
                        <div className="w-64 text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary truncate">{query.query_text}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full
                          ${query.status === 'completed' ? 'text-status-success-light-text bg-status-success-light-bg dark:text-status-success-dark-text dark:bg-status-success-dark-bg' :
                            query.status === 'failed' ? 'text-status-error-light-text bg-status-error-light-bg dark:text-status-error-dark-text dark:bg-status-error-dark-bg' :
                            'text-status-warning-light-text bg-status-warning-light-bg dark:text-status-warning-dark-text dark:bg-status-warning-dark-bg'}`}>
                          {query.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary whitespace-nowrap">
                        {query.execution_time_ms ? `${query.execution_time_ms}ms` : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary whitespace-nowrap">
                        <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full
                          ${query.is_async ? 'text-purple-800 bg-purple-100 dark:text-purple-300 dark:bg-purple-900/30' : 'text-primary-700 bg-primary-100 dark:text-primary-300 dark:bg-primary-900/30'}`}>
                          {query.is_async ? 'Async' : 'Sync'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary whitespace-nowrap">
                        {query.format ? (
                          <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full
                            ${query.format === 'csv' ? 'text-green-800 bg-green-100 dark:text-green-300 dark:bg-green-900/30' : 'text-primary-700 bg-primary-100 dark:text-primary-300 dark:bg-primary-900/30'}`}>
                            {query.format.toUpperCase()}
                          </span>
                        ) : (
                          <span className="inline-flex px-2 text-xs font-semibold leading-5 rounded-full text-primary-700 bg-primary-100 dark:text-primary-300 dark:bg-primary-900/30">PARQUET</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary whitespace-nowrap">
                        {new Date(query.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                        <div className="flex justify-end space-x-4">
                          <Link to={`/queries/${query.id}`} className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
                            View
                          </Link>
                          {onRunQuery ? (
                            <button onClick={() => onRunQuery(query.query_text)}
                              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
                              Run Again
                            </button>
                          ) : (
                            <Link to="/query" state={{ query: query.query_text }}
                              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
                              Run Again
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueryHistoryContent;
